export const hiddenCosts = [
  {
    title: 'Currency Conversion Markup',
    shortTitle: 'Conversion Markup',
    icon: 'ri-exchange-dollar-line',
    desc: 'When your account is in USD but you trade EURGBP, the broker converts your profit/loss at their rate — not the mid-market rate. A 0.3–0.5% markup per conversion means 0.6–1.0% lost per round-turn on cross-currency trades. This cost appears nowhere on your trade ticket.',
    stats: '0.3%–0.5% markup = $30–$50 per $10,000 converted',
    severity: 'High',
    annualCost: '$400–$800 for active traders',
    costMax: 800,
  },
  {
    title: 'Inactivity Fees',
    shortTitle: 'Inactivity',
    icon: 'ri-timer-line',
    desc: 'Many brokers charge $10–$50 per month if your account has no trading activity for 3–12 months. This is a pure drain on idle capital. Even worse: some brokers charge this fee AND continue collecting it until your balance hits zero. Always check the inactivity policy before depositing.',
    stats: '$10–$50/month after 90–365 days of no trading',
    severity: 'Medium',
    annualCost: '$120–$600 for dormant accounts',
    costMax: 600,
  },
  {
    title: 'Withdrawal Fees',
    shortTitle: 'Withdrawal',
    icon: 'ri-bank-line',
    desc: 'Bank wire withdrawals cost $20–$40 per transfer. Credit/debit card withdrawals may carry 2–3% fees. Some brokers offer one free withdrawal per month; others charge every time. If you withdraw profits monthly, withdrawal fees alone can cost $240–$480 per year.',
    stats: '$20–$40 per wire, 2–3% for card withdrawals',
    severity: 'Medium',
    annualCost: '$240–$480 for monthly withdrawals',
    costMax: 480,
  },
  {
    title: 'Spread Widening During Rollover',
    shortTitle: 'Rollover Widening',
    icon: 'ri-moon-clear-line',
    desc: 'Around 5 PM ET, spreads can double or triple for 5–15 minutes as brokers settle positions and liquidity providers pull quotes. A 1-pip EURUSD spread becoming 3 pips for 10 minutes means every trade opened or closed in that window costs $20 extra per lot — and most traders never notice.',
    stats: '2–5× spread widening for 5–15 minutes daily',
    severity: 'High',
    annualCost: '$300–$1,000 for traders active near rollover',
    costMax: 1000,
  },
  {
    title: 'Data Feed & Platform Fees',
    shortTitle: 'Data & Platform',
    icon: 'ri-database-2-line',
    desc: 'Some brokers charge $10–$30/month for Level 2 market depth, advanced charting packages, or API access. Professional traders using multiple platforms or data feeds can pay $50–$200/month in software costs that are separate from trading costs.',
    stats: '$10–$30/month per premium data or platform feature',
    severity: 'Low',
    annualCost: '$120–$360 for data-intensive users',
    costMax: 360,
  },
  {
    title: 'Dividend Adjustments on CFD Indices',
    shortTitle: 'Dividend Adjustments',
    icon: 'ri-funds-line',
    desc: 'When you hold a short position on a CFD index (like US30 or GER40) over the ex-dividend date, the broker deducts the dividend amount from your account. A single index constituent paying a large dividend can trigger a $15–$50 deduction per lot. Long positions receive the dividend, but short positions pay it — and this is often a surprise to traders.',
    stats: '$15–$50 per lot on dividend-paying indices',
    severity: 'Medium',
    annualCost: '$100–$500 for index swing traders',
    costMax: 500,
  },
] as const;

export const conversionScenarios = [
  {
    label: 'USD Account → EURGBP Trade',
    baseCurrency: 'USD',
    tradePair: 'EURGBP',
    tradeSize: 10000,
    markup: 0.004,
    description: 'Every EURGBP trade requires two conversions: entry and exit. The broker applies their rate, not mid-market.',
  },
  {
    label: 'EUR Account → USDJPY Trade',
    baseCurrency: 'EUR',
    tradePair: 'USDJPY',
    tradeSize: 5000,
    markup: 0.003,
    description: 'EUR to JPY conversion via USD — the markup compounds through the intermediate currency.',
  },
  {
    label: 'GBP Account → XAUUSD Trade',
    baseCurrency: 'GBP',
    tradePair: 'XAUUSD',
    tradeSize: 20000,
    markup: 0.005,
    description: 'Gold trades in USD — every profit/loss gets converted back to GBP with the broker\'s rate.',
  },
] as const;

export const hiddenCostsFAQs = [
  {
    question: 'What is a currency conversion markup in forex?',
    answer: 'When your account currency differs from the currency pair\'s profit/loss currency, the broker converts your P&L at their own exchange rate — not the mid-market rate. This "spread on the conversion" is typically 0.3–0.5% per conversion. On a cross-currency trade, you pay this on entry AND exit, totaling 0.6–1.0% of the trade value. A $10,000 position loses $60–$100 just in conversion markup per round-turn, silently.',
    category: 'Conversion Costs',
  },
  {
    question: 'How much do withdrawal fees actually cost per year?',
    answer: 'The math is sobering. Wire transfers cost $20–$40 per withdrawal. If you withdraw profits once per month, that is $240–$480/year. Credit card withdrawals add 2–3% — withdrawing $2,000 costs $40–$60. E-wallet withdrawals (Skrill, Neteller) charge 1–2%. Some brokers offer one free withdrawal per month; always check. For small accounts, withdrawal fees can eat 2–5% of annual returns. Use brokers with free withdrawals and batch your payouts.',
    category: 'Fees',
  },
  {
    question: 'What are inactivity fees and how do I avoid them?',
    answer: 'Inactivity fees kick in after 3–12 months of no trading activity. They range from $10–$50/month and continue until the balance hits zero unless you reactivate the account. To avoid them: (1) make at least one trade every 90 days, (2) withdraw your full balance if you plan to stop trading for an extended period, or (3) choose brokers that do not charge inactivity fees. Some regulated brokers (FCA, ASIC) are required to stop charging after a certain period.',
    category: 'Fees',
  },
  {
    question: 'Why do spreads widen during rollover?',
    answer: 'At 5 PM ET (New York close), brokers settle all open positions for the new trading day. During this 5–15 minute window, liquidity providers pull quotes to adjust their books. The result: spreads widen 2–5× normal levels. EURUSD can jump from 0.8 pips to 2–4 pips. If you open or close a trade during rollover, you pay the widened spread without realizing it. Smart traders avoid trading 10 minutes before and after 5 PM ET.',
    category: 'Timing Costs',
  },
  {
    question: 'Are there any hidden costs in demo accounts?',
    answer: 'Demo accounts are generally free of hidden costs — but they also hide the real costs you will face on a live account. Demos often show idealized spreads, zero slippage, and no conversion markups. This creates a dangerous gap between demo performance and live results. A strategy that looks profitable on demo might be break-even or losing on live once you account for real spreads, commission, slippage, swap, and conversion costs. Always test on a small live account before scaling up.',
    category: 'Basics',
  },
  {
    question: 'What costs do CFD position adjustments create?',
    answer: 'CFD brokers may charge for corporate actions: dividend adjustments on short index positions ($15–$50/lot), stock split adjustments, and merger adjustments. Additionally, some brokers charge a "guaranteed stop loss" premium — 0.3–1.0 pips added to the spread when you use a guaranteed stop. This premium is non-refundable even if your stop is not triggered. Read the broker\'s CFD schedule for a complete list of corporate action charges.',
    category: 'CFD Costs',
  },
  {
    question: 'How do I audit my broker for hidden costs?',
    answer: 'Request a detailed transaction history for the past 3 months. Compare: (1) the conversion rate applied to your P&L vs the mid-market rate at the time — the difference is the markup. (2) Total withdrawal fees paid vs total withdrawals. (3) Any monthly charges labeled "admin," "platform," or "maintenance." (4) Spread widening during rollover — check trades executed between 4:55–5:15 PM ET. If the broker cannot or will not provide this data, that is itself a red flag.',
    category: 'Auditing',
  },
  {
    question: 'Do all brokers charge the same hidden costs?',
    answer: 'No — the variance is huge. ECN brokers typically have lower conversion markups (0.1–0.3%) than market makers (0.5–1.0%). Some brokers offer free withdrawals (one per month), others charge for every method. Inactivity policies range from "no fee ever" to "$50/month after 90 days." Platform fees range from $0 to $50/month. The total annual difference between the cheapest and most expensive broker on hidden costs alone can be $500–$2,000 for an active trader.',
    category: 'Broker Comparison',
  },
  {
    question: 'Can hidden costs turn a winning strategy into a losing one?',
    answer: 'Absolutely. A scalping strategy making 2 pips gross per trade might lose 1 pip to spread, 0.3 pips to slippage, 0.2 pips to conversion markup, and 0.1 pips to platform fees — leaving 0.4 pips net. That is an 80% reduction from gross to net. A swing strategy making 50 pips might lose 2 pips to spread, 1 pip to slippage, 5 pips to swap, and 0.5% to conversion — still profitable but significantly reduced. Always calculate total cost, not just the visible spread.',
    category: 'Strategy Impact',
  },
  {
    question: 'What costs are unique to cryptocurrency CFD trading?',
    answer: 'Crypto CFDs have their own hidden cost ecosystem: overnight financing at 0.05–0.10% per day (18–36% annualized), much wider spreads than forex (BTCUSD often 30–50 pips), and weekend holding costs since crypto trades 24/7 while forex brokers charge swap for all 7 days. Additionally, crypto CFDs often have higher margin requirements and lower maximum leverage. The total cost of holding a crypto CFD for one month can exceed 5% of the position value.',
    category: 'Crypto Costs',
  },
  {
    question: 'How do I compare brokers on total cost, not just spread?',
    answer: 'Create a spreadsheet with these columns for each broker: spread cost (monthly), commission cost, swap cost, conversion markup (estimated at 0.4% of cross-currency volume), withdrawal fees, platform/data fees, and inactivity risk. Sum them all for a true monthly total. A broker with 0.1 pip spread and $7 commission might have $800/month in hidden costs from conversion markups. A broker with 0.8 pip spread and no commission might have $200/month in hidden costs because they charge fewer ancillary fees. The "cheaper" broker on spread is not always the cheaper broker in total.',
    category: 'Broker Comparison',
  },
  {
    question: 'What is the most overlooked hidden cost in forex?',
    answer: 'The conversion markup on cross-currency profit and loss. Most traders never check the exchange rate their broker applies when converting EURGBP profits back to USD. A 0.5% markup sounds small — but it applies to the full position value, not just the profit. On a $100,000 EURGBP position, that is $500 per round-turn in conversion costs alone. Over 100 trades, that is $50,000. This single hidden cost can be larger than spread, commission, swap, and slippage combined for cross-currency traders.',
    category: 'Conversion Costs',
  },
] as const;

export const relatedHiddenCostGuides = [
  {
    title: 'Spread',
    href: '/forex-spread/',
    desc: 'Every trade opens underwater because of the spread. See when it\'s narrowest and how to pick pairs that cost less to trade.',
    icon: 'ri-arrow-left-right-line',
  },
  {
    title: 'Commission',
    href: '/forex-commission/',
    desc: 'Commissions are the most honest cost — you see exactly what you pay. Learn when a commission account actually saves you money.',
    icon: 'ri-bank-card-line',
  },
  {
    title: 'Swap / Rollover',
    href: '/forex-swap/',
    desc: 'Overnight interest can quietly become your biggest expense if you hold for days. Know the rates before you swing trade.',
    icon: 'ri-moon-clear-line',
  },
  {
    title: 'Slippage',
    href: '/forex-slippage/',
    desc: 'You click at one price, you get filled at another. Find out when slippage is avoidable and when it\'s just part of the game.',
    icon: 'ri-speed-up-line',
  },
] as const;

// ---- New data: Trader profiles, broker transparency, audit checklist ----

export const traderProfiles = [
  {
    name: 'Scalper',
    tradesPerDay: 20,
    positionMinutes: '1–15 min',
    subtitle: 'High-frequency, short-hold, tight stops',
    dominantCosts: [
      { cost: 'Spread widening at rollover', percent: 38, color: 'accent' },
      { cost: 'Conversion markup', percent: 28, color: 'primary' },
      { cost: 'Platform & data fees', percent: 18, color: 'secondary' },
      { cost: 'Other', percent: 16, color: 'foreground' },
    ],
    annualEstimate: '$1,800–$3,200',
    insight: 'Scalpers get destroyed by rollover spread widening — their prime trading window overlaps with 5 PM ET if they trade the London/NY crossover. Every trade opened or closed in that 10-minute window pays 2–5× the normal spread.',
    profileIcon: 'ri-flashlight-line',
  },
  {
    name: 'Day Trader',
    tradesPerDay: 5,
    positionMinutes: '15 min–8 hr',
    subtitle: 'Multiple daily rounds, moderate frequency',
    dominantCosts: [
      { cost: 'Conversion markup', percent: 42, color: 'accent' },
      { cost: 'Withdrawal fees', percent: 22, color: 'primary' },
      { cost: 'Spread widening at rollover', percent: 20, color: 'secondary' },
      { cost: 'Other', percent: 16, color: 'foreground' },
    ],
    annualEstimate: '$1,200–$2,600',
    insight: 'Day traders face the perfect storm: multiple round-trips per day amplify conversion markup, and monthly profit withdrawals stack withdrawal fees. A day trader on EURGBP with a USD account loses 4× more to conversion than they realize.',
    profileIcon: 'ri-sun-line',
  },
  {
    name: 'Swing Trader',
    tradesPerDay: 0.5,
    positionDays: '2–30 days',
    subtitle: 'Multi-day holds, fewer entries, larger positions',
    dominantCosts: [
      { cost: 'Dividend adjustments', percent: 35, color: 'accent' },
      { cost: 'Inactivity fees', percent: 25, color: 'primary' },
      { cost: 'Conversion markup', percent: 24, color: 'secondary' },
      { cost: 'Other', percent: 16, color: 'foreground' },
    ],
    annualEstimate: '$600–$1,800',
    insight: 'Swing traders holding index CFDs over ex-dividend dates get surprised by $15–$50 deductions per lot. Combined with inactivity fees during slow months, the "fewer trades = lower cost" assumption breaks down fast when corporate actions kick in.',
    profileIcon: 'ri-moon-clear-line',
  },
] as const;

export const brokerTransparency = [
  {
    brokerType: 'ECN / Direct Market Access',
    firms: 'IC Markets, Pepperstone, FP Markets',
    transparencyScore: 87,
    grade: 'A-',
    headline: 'You pay what you see. Almost.',
    scores: {
      'Conversion markup': { rating: 'Low (0.1–0.3%)', level: 'good', detail: 'Raw spreads + commission model, minimal markup on conversion' },
      'Inactivity fees': { rating: 'None or minimal', level: 'good', detail: 'Most ECN brokers charge zero inactivity fees' },
      'Withdrawal fees': { rating: '1 free/month', level: 'medium', detail: 'One free withdrawal per month, $15–$25 after' },
      'Rollover widening': { rating: 'Moderate (1.5–2.5×)', level: 'medium', detail: 'Liquidity providers pull quotes, but depth is deeper' },
      'Platform fees': { rating: 'Free VPS/API', level: 'good', detail: 'Often included for high-volume traders' },
      'Dividend adjustments': { rating: 'Industry standard', level: 'medium', detail: 'Standard pass-through, transparent schedule' },
    },
    overall: 'Raw spreads plus disclosed commission. Conversion markup is the only cost you might miss — and it is still lower than with other broker types. Best choice if you want to know exactly what you pay.',
    bestFor: 'High-volume scalpers and day traders who trade enough for the raw spread to outweigh the per-lot commission.',
    avoidIf: 'You trade rarely or in micro sizes — the fixed commission per lot eats a thin position alive.',
    verify: 'Ask support for the commission-inclusive cost per standard lot in writing, then compare it head-to-head against a Market Maker\'s all-in spread on your most-traded pair.',
  },
  {
    brokerType: 'Market Maker / Dealing Desk',
    firms: 'FOREX.com, OANDA, IG',
    transparencyScore: 42,
    grade: 'D+',
    headline: 'The spread IS the hiding spot.',
    scores: {
      'Conversion markup': { rating: 'High (0.5–1.0%)', level: 'bad', detail: 'Markup baked into the spread — no separate disclosure' },
      'Inactivity fees': { rating: '$10–$15/month', level: 'medium', detail: 'Charged after 12 months of no activity' },
      'Withdrawal fees': { rating: '$20–$40/wire', level: 'bad', detail: 'Card withdrawals add 2–3%, no free tier on small accounts' },
      'Rollover widening': { rating: 'Significant (2–4×)', level: 'bad', detail: 'Wider spreads during rollover since they control the book' },
      'Platform fees': { rating: 'Free basic, premium $10–30', level: 'medium', detail: 'Basic MT4/5 free, advanced analytics cost extra' },
      'Dividend adjustments': { rating: 'Industry standard', level: 'medium', detail: 'Disclosed in contract specification sheets' },
    },
    overall: 'Markup is baked into every quote. You never get a line item for conversion, withdrawal, or rollover — they are all priced into the spread you accept without questioning. The widest gap between advertised and real cost.',
    bestFor: 'Beginners and low-frequency traders who want one simple all-in spread and no commission math to reconcile.',
    avoidIf: 'You trade cross-currency pairs or large size — the buried conversion markup silently dwarfs the spread you see.',
    verify: 'Pull one month of statements and check the FX rate applied to every P&L conversion against the mid-market rate at that timestamp — the gap is the cost they never printed.',
  },
  {
    brokerType: 'STP / Hybrid',
    firms: 'XM, Tickmill, AvaTrade',
    transparencyScore: 61,
    grade: 'C+',
    headline: 'Inconsistent rules, unpredictable costs.',
    scores: {
      'Conversion markup': { rating: 'Medium (0.3–0.5%)', level: 'medium', detail: 'Variable markup depending on liquidity provider' },
      'Inactivity fees': { rating: '$5/month after 90 days', level: 'bad', detail: 'Some charge inactivity after only 90 days of no trading' },
      'Withdrawal fees': { rating: 'Varies by method', level: 'medium', detail: 'E-wallets 1–2%, wires $20–$30, credit cards 1.5–3%' },
      'Rollover widening': { rating: 'Moderate (1.5–3×)', level: 'medium', detail: 'Depends on liquidity provider mix during rollover' },
      'Platform fees': { rating: 'Free', level: 'good', detail: 'MT4/MT5 included, premium tools may cost extra' },
      'Dividend adjustments': { rating: 'Industry standard', level: 'medium', detail: 'Documented in CFD schedules' },
    },
    overall: 'Hybrid means your trade might hit deep liquidity one day and shallow the next. Costs vary based on which pool your order routes to — and you have no visibility into that routing decision. Platform is free, but inactivity hits fast.',
    bestFor: 'Active traders who stick to major pairs during peak liquidity hours and rarely leave the account idle.',
    avoidIf: 'You go dormant for stretches or need predictable costs — inactivity bites at 90 days and routing changes trade to trade.',
    verify: 'Set a calendar reminder to trade at least once every 90 days to dodge the inactivity fee, and place orders during peak-session hours so you route to the deeper liquidity pool.',
  },
] as const;

export const auditChecklist = [
  {
    step: 1,
    title: 'Request your full transaction history',
    desc: 'Contact broker support and ask for a complete transaction statement covering the last 3–6 months. Specify you want the raw data — including the exchange rate applied to each P&L conversion, not just the net profit/loss.',
    whatToLook: 'Look for two line items per cross-currency trade: the trade P&L in the pair currency, and a separate "Conversion" line with the FX rate used.',
    timeEstimate: '~2 min',
  },
  {
    step: 2,
    title: 'Compare conversion rates to mid-market',
    desc: 'For each conversion entry in your history, look up the mid-market rate at that timestamp (use a site like xe.com). The difference between your broker\'s rate and mid-market is the markup.',
    whatToLook: 'If mid-market was 1.2045 and your broker used 1.2009, the difference is 0.30% — that\'s your hidden conversion cost per trade.',
    timeEstimate: '~3 min',
  },
  {
    step: 3,
    title: 'Audit your withdrawal costs',
    desc: 'Count total withdrawals in the period and total withdrawal fees paid. Divide to get your average cost per withdrawal. Compare against your broker\'s published fee schedule — sometimes you\'re being charged for methods you never chose.',
    whatToLook: 'One trader found they were paying wire fees for "instant bank transfer" withdrawals because their broker auto-routed through a wire network. $25 × 12 withdrawals = $300/year in routing fees alone.',
    timeEstimate: '~1 min',
  },
  {
    step: 4,
    title: 'Check trades executed during rollover',
    desc: 'Filter your trade history for entries/exits between 4:50 PM and 5:20 PM ET. Compare the spread on those trades vs your average spread outside that window.',
    whatToLook: 'If your avg EURUSD spread is 0.8 pips but rollover-window trades show 2.4 pips, the difference (1.6 pips × lot size × number of rollover trades) is your hidden rollover cost.',
    timeEstimate: '~2 min',
  },
  {
    step: 5,
    title: 'Scan for recurring platform or data charges',
    desc: 'Look for any monthly or quarterly deductions labeled "admin fee," "platform fee," "data subscription," or "maintenance." These small charges compound silently.',
    whatToLook: 'A $15/month "advanced charting" fee you forgot about = $180/year. Two of these and you\'re at $360 — before any trading cost.',
    timeEstimate: '~1 min',
  },
  {
    step: 6,
    title: 'Calculate your total hidden cost ratio',
    desc: 'Add up all hidden costs found (conversion markup + excess withdrawal fees + rollover spread loss + platform fees + inactivity charges) and divide by your total trading volume. This is your true cost ratio.',
    whatToLook: 'A trader paying 0.6 pip visible spread + $3.5 commission per lot might think they have a 0.95 pip all-in. After adding 0.3 pip conversion markup, 0.1 pip rollover loss, and platform fees, the real all-in might be 1.5 pips — a 58% increase from what they budgeted.',
    timeEstimate: '~2 min',
  },
] as const;